import React from 'react';
import { useParams } from 'react-router-dom';
import { useTutorial } from '../hooks/useTutorial';
import { useTutorialCategories } from '../hooks/useTutorialCategories';
import TutorialPage from './Tutorials/TutorialPage';
import TutorialsByCategoryPage from './Tutorials/TutorialsByCategoryPage';
import { Loader2, BookOpen } from 'lucide-react';

export default function TutorialRouter() {
  const { potentialSlug } = useParams<{ potentialSlug: string }>();
  
  // Debug: Log the slug value immediately
  console.log('TutorialRouter: potentialSlug from useParams:', potentialSlug);
  
  // Always load categories to determine route type
  const { categories, loading: categoriesLoading, error: categoriesError } = useTutorialCategories();
  
  // Determine if this slug is a category
  const isCategorySlug = React.useMemo(() => {
    if (!categories || !potentialSlug) return false;
    return categories.some(cat => cat.category_slug === potentialSlug);
  }, [categories, potentialSlug]);
  
  // Only fetch tutorial if we know it's not a category
  const shouldFetchTutorial = !categoriesLoading && !isCategorySlug;
  const { tutorial, loading: tutorialLoading, error: tutorialError } = useTutorial(
    potentialSlug // Always pass the slug, let useTutorial handle the logic
  );

  // Debug: Log tutorial state in router
  console.log('TutorialRouter: Tutorial state -', {
    potentialSlug,
    isCategorySlug,
    tutorial: tutorial ? { id: tutorial.id, title: tutorial.title, slug: tutorial.slug } : null,
    tutorialLoading,
    tutorialError
  });

  // Show loading while categories are loading
  if (categoriesLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  // Handle categories error
  if (categoriesError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Content</h1>
            <p className="text-gray-600 mb-6">
              Unable to load tutorial categories.
            </p>
            <a
              href="/tutorials"
              className="inline-block bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors"
            >
              Back to Tutorials
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Render category page if slug matches a category
  if (isCategorySlug) {
    return <TutorialsByCategoryPage />;
  }

  // Show loading while tutorial is loading
  if (tutorialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  // Render tutorial page if tutorial was found
  if (!tutorialLoading && tutorial && !tutorialError) {
    return <TutorialPage slug={potentialSlug} />;
  }

  // Show 404 only when loading is complete and no tutorial found
  if (!tutorialLoading && !tutorial) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Tutorial Not Found</h1>
            <p className="text-gray-600 mb-6">
              The tutorial or category you're looking for doesn't exist.
            </p>
            <div className="space-y-2">
              <a
                href="/tutorials"
                className="inline-block bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors"
              >
                Browse All Tutorials
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading spinner while tutorial is loading
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
    </div>
  );
}